import { ItemRequest, Reminder, Task, Trouble } from '../../models/dashboard-models';
import { useTranslation } from 'react-i18next';
import React, { Suspense, useContext, useState } from 'react';
import { Button, Modal, Spinner, Table } from 'react-bootstrap';
import { NothingSelectedComponent } from '../NothingSelectedComponent';
import { HandoverProps, PassportType } from '../../models/warehouse-models';
import { getHandover, getHandoverByTaskId } from '../../utils/warehouses-hooks';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../CustomToastContent';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import dateFormat from 'dateformat';
import { date, dateTime } from '../../utils/Enums/Common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle';
import ErrorBoundary from '../ErrorBoundary';
import { RenderTaskImage } from '../../pages/Tasks';
import { RequiredPermissions } from '../RequiredPermissions';
import { Role } from '../../utils/Enums/Roles';
import { CustomDropdown } from '../Dashboard/CustomDropdown';
import { useGetUsers } from '../../utils/users-hooks';
import { useAsyncResource } from 'use-async-resource';
import { changeTaskAssignee } from '../../utils/work-hooks';
import { AuthContext } from '../../context/AuthContext';

export const TaskDetails = ({
    task,
    selectedTaskId,
    completeSelectedTask,
    deleteSelectedTask,
    isDeletingTask = false,
    refetchTasks,
    selectTask,
}: {
    task: Task;
    selectedTaskId: number;
    completeSelectedTask: (task: Task) => void;
    deleteSelectedTask?: (task: Task) => void;
    isDeletingTask?: boolean;
    refetchTasks: () => void;
    selectTask: (id: number) => void;
}) => {
    const { group } = useContext(AuthContext);

    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tableT } = useTranslation('translation', { keyPrefix: 'table' });
    const [showChangeAssignee, setShowChangeAssignee] = useState(false);
    const [getDistributors] = useAsyncResource(
        useGetUsers,
        group.mainGroup !== undefined
            ? { filter: { tags: [], search: group.mainGroup + '-' + group.subGroup } }
            : undefined,
        'DISTRIBUTOR,INSTALLER,COORDINATOR,ADMIN,WAREHOUSE,FINANCIAL'
    );
    const distributors = getDistributors().items.filter(
        (distributor) =>
            (distributor.roles.includes(Role.DISTRIBUTOR) || distributor.roles.includes(Role.INSTALLER)) &&
            !distributor.roles.includes(Role.ADMIN)
    );
    const [taskNewAssignee, setTaskNewAssignee] = useState(task.assignee?.id);
    const branch = task.branchVO;

    const printDocument = (blob: Blob) => {
        if (blob) {
            const pdf = URL.createObjectURL(blob);
            window.open(pdf, '_blank');
        } else {
            toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText />, toastOptionsTopRight);
        }
    };
    const printHandover = async (task: Task) => {
        if (task.assigneeId) {
            let handover;
            try {
                handover = await getHandoverByTaskId(task.id);
            } catch (e) {
                toast.error(
                    <CustomToastContent content={[`${t('noProtocolForTask')}`]} whiteText />,
                    toastOptionsTopRight
                );
                return;
            }
            const props: HandoverProps = {
                id: handover.id,
                date: dateFormat(new Date(), 'dd.mm.yyyy'),
                town: handover.city,
                representative: handover.clientRepresentative,
                taskItemsVO: {
                    branchId: handover.branchId,
                    userId: handover.distributorId,
                    neededItems: handover.items,
                    taskId: handover.taskId,
                    taskCategory: handover.taskCategory,
                },
                save: false,
                signature: handover.signature,
            };
            const blob = await getHandover(props);
            return printDocument(blob);
        }
    };

    const changeAssignee = (entitie: string) => {
        const currentDistributor = distributors.find((distributor) => distributor?.id?.toString() === entitie);

        setTaskNewAssignee(currentDistributor?.id);
    };

    return (
        <>
            <Modal onHide={() => setShowChangeAssignee(false)} show={showChangeAssignee} size='sm' centered>
                <Modal.Body>
                    <CustomDropdown
                        entities={distributors.map((distributor) => {
                            return { label: distributor.name, value: `${distributor?.id}` };
                        })}
                        setEntities={changeAssignee}
                        label={t('distributor')}
                        defaultValue={task.assignee?.id.toString()}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        className='bg-success'
                        onClick={async () => {
                            await changeTaskAssignee(task.id, taskNewAssignee);
                            setShowChangeAssignee(false);
                            selectTask(-1);
                            refetchTasks();
                        }}
                    >
                        {t('change')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <div className='bg-white p-3 w-100 m-0 mb-2 border '>
                {task ? (
                    <div className='d-flex flex-column align-items-center '>
                        <h4 className='m-3 mb-4'>{t('taskDetails')}</h4>

                        <div className='d-flex w-100 align-items-end flex-wrap-reverse'>
                            <div className='flex-grow-1 me-2'>
                                <div className='mb-5'>
                                    <InfoRow label={tableT('forBranch')} value={branch.name} />
                                    <InfoRow label={tableT('phone')} value={branch.phone || ''} />
                                    <InfoRow
                                        label={t('address')}
                                        value={`${branch.location.city} ${branch.location.address}`}
                                    />
                                </div>
                                <InfoRow label={tableT('companyName')} value={branch.client.companyName} />
                                <InfoRow label={tableT('phone')} value={branch.client.phone} />
                            </div>
                            <div className='flex-grow-1 me-2'>
                                <InfoRow
                                    label={t('assignedTo')}
                                    value={distributors.find((value) => value.id == task.assigneeId)?.name ?? ''}
                                />
                                {task.is_done && (
                                    <InfoRow label={t('ends')} value={dateFormat(task.completion_time, dateTime)} />
                                )}
                                <InfoRow
                                    label={tableT('type')}
                                    value={task.isTest ? t('TEST') : t(task.taskCategory)}
                                />
                                <InfoRow
                                    label={t('assignedBy')}
                                    value={
                                        getDistributors().items.find((value) => value.id == task.coordinatorId)?.name ??
                                        t('system')
                                    }
                                />
                                <InfoRow label={tableT('createdAt')} value={dateFormat(task.creation_time, dateTime)} />
                                {task.reminders.length > 0 && <BranchReminderList list={task.reminders} />}
                                {task.troubleTicket && <TroubleTicket trouble={task.troubleTicket} />}
                            </div>

                            <ErrorBoundary fallback={<h5 className='mb-2'>Error</h5>}>
                                <Suspense fallback={<Spinner animation='border' />}>
                                    {task.id == selectedTaskId && (
                                        <RenderTaskImage branchId={task.branchId} branch={branch} />
                                    )}
                                </Suspense>
                            </ErrorBoundary>
                        </div>
                        <div className='d-flex w-100 mb-2'>
                            {task.details && task.details.length > 0 ? (
                                <div className='w-50 flex-grow-1'>
                                    <h5 className='my-3 mx-0'>{t('details')}</h5>
                                    <div className='rounded border-secondary border p-3'>{task.details}</div>
                                </div>
                            ) : (
                                <></>
                            )}
                            {task.neededItems && task.neededItems.length > 0 && (
                                <RequiredItemsTable neededItems={task.neededItems} />
                            )}
                        </div>
                        <div className='d-flex align-items-center justify-content-around w-100 flex-wrap'>
                            <div className='d-flex mb-2 justify-content-evenly flex-grow-1'>
                                {task.is_done && <Button onClick={() => printHandover(task)}>{t('handover')}</Button>}
                                {/*<Button onClick={() => printWarehouseReceipt(task)}>{t('warehouseReceipt')}</Button>*/}
                                {!task.is_done && (
                                    <Button onClick={() => setShowChangeAssignee(true)}>{t('changeAssignee')}</Button>
                                )}
                            </div>
                            <div className='d-flex justify-content-evenly flex-grow-1'>
                                {!task.is_done && (
                                    <Button onClick={() => completeSelectedTask(task)} className='bg-success '>
                                        {t('completeTask')}
                                    </Button>
                                )}
                                {deleteSelectedTask && (
                                    <RequiredPermissions requiredPermissions={[Role.COORDINATOR]}>
                                        <Button
                                            onClick={() => deleteSelectedTask(task)}
                                            className='bg-danger'
                                            disabled={isDeletingTask}
                                        >
                                            {isDeletingTask ? (
                                                <Spinner animation='border' size='sm' />
                                            ) : (
                                                t('deleteTask')
                                            )}
                                        </Button>
                                    </RequiredPermissions>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <NothingSelectedComponent nothingSelectedText={t('tasks')} />
                    </div>
                )}
            </div>
        </>
    );
};

const RequiredItemsTable = ({ neededItems }: { neededItems: ItemRequest[] }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    return (
        <div className='w-50 flex-grow-1'>
            <h5 className='my-2 '>{t('neededItems')}</h5>
            <Table striped hover responsive className='mt-2 border border-dark'>
                <thead>
                    <tr>
                        <th className='fw-bold w-25 text-center'>{t('materials')}</th>
                        <th className='fw-bold w-25 text-center'>{t('type')}</th>
                        <th className='fw-bold w-25 text-center'>{t('quantity')}</th>
                    </tr>
                </thead>
                <tbody>
                    {neededItems.map((item, index) => (
                        <tr key={index}>
                            <td className='text-center'>{item.itemName}</td>
                            <td className='text-center'>{item.itemType}</td>
                            <td className='text-center'>{item.itemCount}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => {
    return (
        <div className='d-flex w-100 align-items-center justify-content-between px-2 py-2 border-top'>
            <h6 className='text-secondary'>{label}:</h6>
            <h6 className='ps-1'>{value}</h6>
        </div>
    );
};

const BranchReminderList = ({ list }: { list: Reminder[] }) => {
    return (
        <>
            {list.map((reminder, index) => (
                <div className='d-flex flex-column w-100 p-1 reminder my-2' key={index}>
                    <div className='d-flex justify-content-between align-items-center w-100 p-1'>
                        <FontAwesomeIcon icon={faBell} className='fa-fw' color='orange' />
                        <div className='d-flex align-items-center'>
                            <span className='mb-0 px-1'>Ends {dateFormat(reminder.endTime, date)}</span>
                        </div>
                    </div>
                    <div className='p-1'>
                        <p className='fw-bold mb-0'>{reminder.comment}</p>
                    </div>
                </div>
            ))}
        </>
    );
};

function TroubleTicket({ trouble }: { trouble: Trouble }) {
    return (
        <div className='d-flex flex-column w-100 trouble rounded my-2'>
            <div className='d-flex justify-content-between align-items-center w-100 p-1'>
                <h3>{trouble.title}</h3>
                <FontAwesomeIcon icon={faExclamationTriangle} className='fa-fw' color='red' />
            </div>
            <div className='p-1'>
                <p className='fw-bold mb-0'>{trouble.description}</p>
            </div>
        </div>
    );
}
